import React, { useCallback, useEffect, useState } from "react";
import { Icon, Modal } from "antd";
import { Link } from "react-router-dom";
import { getData, deleteData } from "../../util/API";
import { HIDDEN_ROLES } from "../KycVerificationNotifications/constants";
import "./CoordinatorAlerts.less";

const POLL_MS = 60 * 1000;

/**
 * Coordinator alerts for work orders blocked on partner bank details.
 *
 * Two deliberate differences from KycVerificationNotifications, which this used to copy:
 *
 *  - The pill never hides. There is no dismiss and no "hidden" flag in localStorage, because a
 *    waiting alert IS the 30-minute reminder -- letting the coordinator hide it would lose the
 *    thing we were asked to remind them about. It clears itself the moment the account number
 *    arrives, by the partner submitting the form or a coordinator typing it in.
 *  - Opening it is a centred modal box, not a corner panel, so the reminder is actually read.
 *
 * In-app rather than WhatsApp or SMS because it repeats: anything per-message would cost money
 * every 30 minutes.
 */
const CoordinatorAlerts = () => {
  const userRole = localStorage.getItem("user_role") || "";
  const userUuid =
    localStorage.getItem("user_uuid") || localStorage.getItem("uuid");
  const enabled =
    Boolean(localStorage.getItem("token")) && !HIDDEN_ROLES.has(userRole) && Boolean(userUuid);

  const [waiting, setWaiting] = useState([]);
  const [issued, setIssued] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchAlerts = useCallback(() => {
    if (!enabled) return;
    // Computed live by the backend, like the KYC panel: nothing is stored, so a waiting alert
    // disappears by itself once the account number arrives.
    getData("/management/coordinator-wo-alerts/")
      .then((res) => {
        setWaiting(Array.isArray(res?.data?.waiting) ? res.data.waiting : []);
        setIssued(Array.isArray(res?.data?.issued) ? res.data.issued : []);
      })
      .catch(() => {
        // Never disrupt the page the coordinator is working on.
      });
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    fetchAlerts();
    const timer = setInterval(fetchAlerts, POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, fetchAlerts]);

  // Only issued alerts can be dismissed. A waiting one clears itself when the account number
  // arrives; hiding it early would lose the reminder.
  const dismiss = (id) => {
    setIssued((rows) => rows.filter((r) => r.id !== id));
    deleteData("/management/coordinator-wo-alerts/", { alert_id: id }).catch(() => {});
  };

  const total = waiting.length + issued.length;
  // Unmounts the sheet too, so the coordinator is never left on an empty screen once the last
  // alert resolves.
  if (!enabled || !total) return null;

  return (
    <>
      <div className="wo-alert-dock">
        <button
          type="button"
          className="wo-alert-pill"
          onClick={() => setOpen(true)}
          aria-label={`Partner bank details: ${waiting.length} pending, ${issued.length} issued`}
        >
          <Icon type="exclamation-circle" />
          <span>Bank details pending</span>
          <span className="wo-alert-pill-count">{total > 99 ? "99+" : total}</span>
        </button>
      </div>

      <Modal
        visible={open}
        onCancel={() => setOpen(false)}
        footer={null}
        closable={false}
        centered
        width={820}
        wrapClassName="wo-alert-modal-wrap"
        destroyOnClose
      >
        <header className="wo-alert-head">
          <div>
            <div className="wo-alert-head-title">
              <Icon type="exclamation-circle" /> Partner bank details pending
            </div>
            <div className="wo-alert-head-sub">
              {waiting.length} waiting on bank details
              {issued.length ? ` · ${issued.length} issued` : ""}
            </div>
          </div>
          <button
            type="button"
            className="wo-alert-close"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            <Icon type="close" />
          </button>
        </header>

        <div className="wo-alert-body">
          {waiting.length > 0 && (
            <div className="wo-alert-section-title">Waiting on the partner</div>
          )}
          {waiting.map((a) => (
            <article key={`w-${a.id}`} className="wo-alert-card">
              <div className="wo-alert-card-main">
                <div className="wo-alert-card-name">{a.partner}</div>
                <div className="wo-alert-card-text">
                  The partner has not yet filled in the bank details form, so the work order for{" "}
                  <span className="wo-alert-card-ticket">{a.ticket_number}</span> has not been
                  issued. Follow up with the partner, or add the account number yourself.
                </div>
                {a.ticket_uuid && (
                  <Link
                    to={`/ticket-details/${a.ticket_uuid}`}
                    className="wo-alert-card-link"
                    onClick={() => setOpen(false)}
                  >
                    Open ticket <Icon type="arrow-right" />
                  </Link>
                )}
              </div>
            </article>
          ))}

          {issued.length > 0 && (
            <div className="wo-alert-section-title">Issued</div>
          )}
          {issued.map((a) => (
            <article key={`i-${a.id}`} className="wo-alert-card wo-alert-card--done">
              <div className="wo-alert-card-main">
                <div className="wo-alert-card-name">{a.partner}</div>
                <div className="wo-alert-card-text">
                  Bank details received — work order {a.work_order_number} issued for{" "}
                  <span className="wo-alert-card-ticket">{a.ticket_number}</span>.
                </div>
                {a.ticket_uuid && (
                  <Link
                    to={`/ticket-details/${a.ticket_uuid}`}
                    className="wo-alert-card-link"
                    onClick={() => setOpen(false)}
                  >
                    Open ticket <Icon type="arrow-right" />
                  </Link>
                )}
              </div>
              <button
                type="button"
                className="wo-alert-card-dismiss"
                onClick={() => dismiss(a.id)}
                aria-label="Dismiss"
              >
                <Icon type="close" />
              </button>
            </article>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default CoordinatorAlerts;
